import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { AgentHooksService } from '../../services/agent-hooks.service';
import {
  AgentConnectionTest,
  AgentEvent,
  AgentHook,
  AgentHookDelivery,
  AgentIntegration,
  AgentTrigger
} from '../../interfaces/agent-hooks.interface';

/**
 * Agent hooks (admin): connects this app to DPE and chooses which agent handles which of the app's events. The app's code only
 * announces events; everything here is configuration, so a new agent made in DPE is connected without a release.
 */
@Component({
  selector: 'app-agent-hooks',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agent-hooks.component.html',
  styleUrls: ['./agent-hooks.component.css']
})
export class AgentHooksComponent implements OnInit {
  @Output() backClick = new EventEmitter<void>();

  isLoading = true;
  forbidden = false;
  error = '';

  events: AgentEvent[] = [];
  integration: AgentIntegration | null = null;
  hooks: AgentHook[] = [];
  deliveries: AgentHookDelivery[] = [];
  /** The triggers DPE offers, known after a successful connection test. */
  catalog: AgentTrigger[] = [];

  addressInput = '';
  keyInput = '';
  isSavingConnection = false;
  isTesting = false;
  test: AgentConnectionTest | null = null;

  newEvent = '';
  newTrigger = '';
  newForward = true;
  isAdding = false;
  busyHookId: string | null = null;
  openSample: string | null = null;

  constructor(private service: AgentHooksService, private toastr: ToastrService) {}

  ngOnInit(): void {
    this.reload();
  }

  onBack(): void {
    this.backClick.emit();
  }

  get connected(): boolean {
    return !!this.integration?.dpeApiBaseUrl && (this.integration?.hasKey ?? false);
  }

  reload(): void {
    this.isLoading = true;
    this.error = '';
    forkJoin({
      events: this.service.events(),
      integration: this.service.integration(),
      hooks: this.service.hooks(),
      deliveries: this.service.deliveries()
    }).subscribe({
      next: ({ events, integration, hooks, deliveries }) => {
        this.events = events.data ?? [];
        this.integration = integration.data ?? null;
        this.addressInput = this.integration && !this.integration.usingDefaultAddress ? this.integration.dpeApiBaseUrl : '';
        this.hooks = hooks.data ?? [];
        this.deliveries = deliveries.data ?? [];
        if (!this.newEvent && this.events.length) this.newEvent = this.events[0].name;
        this.isLoading = false;
        // With a key saved, ask DPE what it offers so the trigger list is ready.
        if (this.connected) this.runTest(true);
      },
      error: (err) => {
        this.isLoading = false;
        if (err?.status === 403) this.forbidden = true;
        else this.error = err?.error?.message || 'Could not load the agent hooks.';
      }
    });
  }

  // ── connection ──
  saveConnection(): void {
    this.isSavingConnection = true;
    this.service.saveIntegration({
      dpeApiBaseUrl: this.addressInput.trim(),
      appKey: this.keyInput.trim() || undefined
    }).subscribe({
      next: (res) => {
        this.isSavingConnection = false;
        this.integration = res.data ?? this.integration;
        this.keyInput = '';
        this.toastr.success('Connection saved.');
        this.runTest(false);
      },
      error: (err) => {
        this.isSavingConnection = false;
        this.toastr.error(err?.error?.message || 'Could not save the connection.');
      }
    });
  }

  removeKey(): void {
    if (!confirm('Remove the saved connection key? Agents stop receiving events until a new key is saved.')) return;
    this.service.saveIntegration({ clearKey: true }).subscribe({
      next: (res) => { this.integration = res.data ?? this.integration; this.test = null; this.catalog = []; },
      error: (err) => this.toastr.error(err?.error?.message || 'Could not remove the key.')
    });
  }

  runTest(quiet: boolean): void {
    this.isTesting = true;
    this.service.testConnection().subscribe({
      next: (res) => {
        this.isTesting = false;
        this.test = res.data ?? null;
        this.catalog = this.test?.ok ? this.test.triggers : [];
        if (!quiet && this.test) (this.test.ok ? this.toastr.success : this.toastr.warning).call(this.toastr, this.test.message);
      },
      error: (err) => {
        this.isTesting = false;
        this.test = { ok: false, message: err?.error?.message || 'The test could not run.', triggers: [] };
        this.catalog = [];
      }
    });
  }

  // ── hooks ──
  get canAdd(): boolean {
    return !!this.newEvent && !!this.newTrigger.trim() && !this.isAdding;
  }

  addHook(): void {
    if (!this.canAdd) return;
    this.isAdding = true;
    this.service.createHook({ eventName: this.newEvent, triggerSlug: this.newTrigger.trim(), forwardUserToken: this.newForward }).subscribe({
      next: (res) => {
        this.isAdding = false;
        if (res.data) this.hooks = [...this.hooks, res.data];
        this.newTrigger = '';
        this.toastr.success('Hook added. New events now go to that agent.');
      },
      error: (err) => {
        this.isAdding = false;
        this.toastr.error(err?.error?.message || 'Could not add the hook.');
      }
    });
  }

  change(hook: AgentHook, change: { enabled?: boolean; forwardUserToken?: boolean; triggerSlug?: string }): void {
    this.busyHookId = hook.id;
    this.service.updateHook(hook.id, change).subscribe({
      next: (res) => {
        this.busyHookId = null;
        if (res.data) this.hooks = this.hooks.map((h) => (h.id === hook.id ? res.data! : h));
      },
      error: (err) => {
        this.busyHookId = null;
        this.toastr.error(err?.error?.message || 'Could not save the change.');
        this.hooks = [...this.hooks]; // put the control back to what is stored
      }
    });
  }

  remove(hook: AgentHook): void {
    if (!confirm(`Stop sending "${this.eventTitle(hook.eventName)}" to "${hook.triggerSlug}"?`)) return;
    this.service.deleteHook(hook.id).subscribe({
      next: () => (this.hooks = this.hooks.filter((h) => h.id !== hook.id)),
      error: (err) => this.toastr.error(err?.error?.message || 'Could not remove the hook.')
    });
  }

  sendTest(hook: AgentHook): void {
    this.busyHookId = hook.id;
    this.service.testHook(hook.id).subscribe({
      next: (res) => {
        this.busyHookId = null;
        const d = res.data;
        if (d?.status === 'sent') this.toastr.success('Sent. The agent is working on the sample: see Recent activity.');
        else this.toastr.warning(d?.error || 'The sample was not delivered.');
        this.refreshDeliveries();
      },
      error: (err) => {
        this.busyHookId = null;
        this.toastr.error(err?.error?.message || 'Could not send the sample.');
      }
    });
  }

  refreshDeliveries(): void {
    this.service.deliveries().subscribe({ next: (res) => (this.deliveries = res.data ?? []) });
  }

  // ── display ──
  eventTitle(name: string): string {
    return this.events.find((e) => e.name === name)?.title ?? name;
  }

  eventDescription(name: string): string {
    return this.events.find((e) => e.name === name)?.description ?? '';
  }

  sampleJson(name: string): string {
    const sample = this.events.find((e) => e.name === name)?.sample;
    return sample === undefined ? '' : JSON.stringify(sample, null, 2);
  }

  trigger(slug: string): AgentTrigger | undefined {
    return this.catalog.find((t) => t.slug === slug);
  }

  /** True when the catalog is known and does not list the hook's trigger: the agent may have been renamed or removed in DPE. */
  missingInCatalog(hook: AgentHook): boolean {
    return this.catalog.length > 0 && !this.trigger(hook.triggerSlug);
  }

  toggleSample(name: string): void {
    this.openSample = this.openSample === name ? null : name;
  }

  ago(iso: string): string {
    const seconds = Math.max(0, Math.round((Date.now() - new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).getTime()) / 1000));
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
    return new Date(iso).toLocaleString();
  }
}
